import { useState, useRef } from "react";
import { toast } from "sonner";
import { uploadImage, resolveImageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, X, Loader2 } from "lucide-react";

export default function ImageUpload({ value, onChange, testId }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const resolved = resolveImageUrl(value);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error("Please pick an image file");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Max 8 MB");
      return;
    }
    setUploading(true);
    try {
      const { url } = await uploadImage(file);
      onChange(url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="mt-2">
      {resolved ? (
        <div className="relative inline-block rounded-lg overflow-hidden border border-zinc-200 group">
          <img src={resolved} alt="preview" className="h-40 w-auto max-w-xs object-cover" />
          <button
            type="button"
            onClick={() => onChange(null)}
            className="absolute top-2 right-2 h-7 w-7 grid place-items-center rounded-full bg-black/70 text-white hover:bg-black"
            aria-label="Remove image"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid={testId}
          className="flex items-center justify-center gap-2 w-full h-32 rounded-lg border-2 border-dashed border-zinc-300 hover:border-zinc-900 text-zinc-500 hover:text-zinc-900 transition-colors"
        >
          {uploading ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>
          ) : (
            <><Upload className="w-4 h-4" /> Click to upload image (JPG/PNG/WebP · max 8MB)</>
          )}
        </button>
      )}
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={handle} />
    </div>
  );
}
